import { useActionSheet } from '@expo/react-native-action-sheet';
import * as DocumentPicker from 'expo-document-picker';
import { Image } from 'expo-image';
import * as ImagePicker from 'expo-image-picker';
import { useRouter } from 'expo-router';
import { useState } from 'react';
import {
  Keyboard,
  Platform,
  Pressable,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import {
  type ReportAttachment,
  useCreateReport,
} from '@/api/partners/reports/use-create-report';
import { WHITE } from '@/style/colors';
import { Button, Text } from '@/ui';
import { Icon } from '@/ui/icon';
import { Cancel } from '@/ui/icons/cancel';
import { InputMulti } from '@/ui/input-multi';

const normalizeWebDocumentAsset = async (
  asset: DocumentPicker.DocumentPickerAsset
): Promise<ReportAttachment> => {
  if (!asset.name || !asset.mimeType) {
    throw new Error('Web upload requires file name and mime type.');
  }

  const response = await fetch(asset.uri);
  const blob = await response.blob();
  const file = new File([blob], asset.name, { type: asset.mimeType });

  return {
    uri: asset.uri,
    name: asset.name,
    type: asset.mimeType,
    file,
  };
};

const normalizeNativeDocumentAsset = (
  asset: DocumentPicker.DocumentPickerAsset
): ReportAttachment => {
  if (!asset.name || !asset.mimeType) {
    throw new Error('Native document upload requires file name and mime type.');
  }

  return {
    uri: asset.uri,
    name: asset.name,
    type: asset.mimeType,
  };
};

const normalizeImagePickerAsset = (
  asset: ImagePicker.ImagePickerAsset
): ReportAttachment => {
  if (!asset.fileName || !asset.mimeType) {
    throw new Error('Native image upload requires file name and mime type.');
  }

  return {
    uri: asset.uri,
    name: asset.fileName,
    type: asset.mimeType,
  };
};

export default function SubmitFeedback() {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const { showActionSheetWithOptions } = useActionSheet();
  const [feedback, setFeedback] = useState('');
  const [image, setImage] = useState<ReportAttachment>();
  const [uploadingPhotos, setUploadingPhotos] = useState(false);

  const { mutateAsync: createReport, isPending: isCreatingReport } =
    useCreateReport();

  const handleSubmitFeedback = async () => {
    try {
      await createReport({
        description: feedback,
        file: image,
      });

      Toast.show({
        text1: 'Feedback reported!',
        type: 'success',
      });
      router.navigate('/partner/more');
    } catch (error: any) {
      console.error('Error updating:', error);
    }
  };

  const handleUploadPhotos = async () => {
    if (Platform.OS === 'web') {
      try {
        const result = await DocumentPicker.getDocumentAsync({
          type: ['image/jpeg', 'image/png', 'image/heic'],
          copyToCacheDirectory: false,
          multiple: false,
        });

        if (!result.canceled) {
          const assets = await Promise.all(
            result.assets.map((asset) => normalizeWebDocumentAsset(asset))
          );

          setImage(assets[0]);
        }
      } catch (error) {
        console.error('Error picking file:', error);
      }

      return;
    }

    const options = [
      'Choose from Photos',
      // 'Take a Photo',
      'Upload Files',
      'Cancel',
    ];
    const selectPhotoIdx = 0;
    // const takeAPhotoIdx = 1;
    const uploadFilesIdx = 1;
    const cancelButtonIndex = 2;

    showActionSheetWithOptions(
      {
        options,
        cancelButtonIndex,
        destructiveButtonIndex: cancelButtonIndex,
      },
      async (selectedIndex: number) => {
        switch (selectedIndex) {
          case uploadFilesIdx: {
            try {
              setUploadingPhotos(true);

              const result = await DocumentPicker.getDocumentAsync({
                type: ['image/jpeg', 'image/png', 'image/heic'],
                copyToCacheDirectory: false,
                multiple: false,
              });

              if (!result.canceled) {
                setImage(normalizeNativeDocumentAsset(result.assets[0]));
              }
            } catch (error) {
              console.error('Error picking file:', error);
            } finally {
              setUploadingPhotos(false);
            }
            break;
          }
          case selectPhotoIdx: {
            setUploadingPhotos(true);

            const result = await ImagePicker.launchImageLibraryAsync({
              mediaTypes: ImagePicker.MediaTypeOptions.Images,
              allowsEditing: false,
              allowsMultipleSelection: false,
              base64: true,
              selectionLimit: 15,
            });

            if (!result.canceled) {
              setImage(normalizeImagePickerAsset(result.assets[0]));
            }

            setUploadingPhotos(false);
            break;
          }

          case cancelButtonIndex:
          // Canceled
        }
      }
    );
  };

  const content = (
    <View style={{ flexGrow: 1, gap: 64 }}>
      <View style={styles.nav}>
        <Text type="title1">Submit Feedback</Text>
      </View>

      <View
        style={{
          flexGrow: 1,
          justifyContent: 'space-between',
          paddingBottom: 24,
        }}
      >
        <View style={{ gap: 24, flexGrow: 1 }}>
          <View style={{ gap: 32 }}>
            <InputMulti
              label="Your feedback"
              text={feedback || ''}
              onChangeText={(s: string) => setFeedback(s)}
              placeholder="Tell us what's on your mind"
              style={styles.input}
            />

            {image ? (
              <View style={{ width: 60, height: 60 }}>
                <Pressable
                  hitSlop={8}
                  onPress={() => {
                    setImage(undefined);
                  }}
                  style={{ position: 'absolute', top: -10, right: 0 }}
                >
                  <Icon
                    type="naked"
                    icon={<Cancel />}
                    fillColor={WHITE}
                    width={8}
                    height={8}
                  />
                </Pressable>

                <Image
                  source={{ uri: image?.uri }}
                  style={{ width: 60, height: 60 }}
                  contentFit="contain"
                />
              </View>
            ) : null}

            <Button
              text="Attach file"
              type="secondarySmall"
              onPress={handleUploadPhotos}
              style={{ width: 125 }}
            />
          </View>

          <View style={styles.buttonContainerDesktop}>
            <Button
              text="Submit"
              type="primary"
              onPress={handleSubmitFeedback}
              loading={isCreatingReport}
              disabled={!feedback || uploadingPhotos || isCreatingReport}
              style={{ width: 100 }}
            />
          </View>
        </View>

        <View style={styles.buttonContainerMobile}>
          <Button
            text="Submit"
            type="primary"
            onPress={handleSubmitFeedback}
            loading={isCreatingReport}
            disabled={!feedback || uploadingPhotos || isCreatingReport}
          />
        </View>
      </View>
    </View>
  );

  return (
    <View
      style={{
        flex: 1,
        backgroundColor: theme.colors.background_1,
        paddingTop: 16,
      }}
    >
      <KeyboardAwareScrollView
        style={styles.scrollView}
        contentContainerStyle={{
          height: '100%',
        }}
        keyboardShouldPersistTaps="always"
      >
        {Platform.OS === 'web' ? (
          content
        ) : (
          <TouchableWithoutFeedback
            onPress={() => {
              Keyboard.dismiss();
            }}
            accessible={false}
          >
            {content}
          </TouchableWithoutFeedback>
        )}
      </KeyboardAwareScrollView>
    </View>
  );
}

const stylesheet = createStyleSheet((_theme) => ({
  scrollView: {
    flexGrow: 1,
    borderRadius: 16,
    paddingLeft: {
      xs: 16,
      md: 56,
    },
    paddingRight: {
      xs: 16,
      md: 56,
    },
    paddingTop: {
      xs: 16,
      md: 56,
    },
  },
  nav: {
    display: {
      xs: 'none',
      md: 'flex',
    },
    gap: 16,
  },
  buttonContainerMobile: {
    display: {
      xs: 'flex',
      md: 'none',
    },
  },
  buttonContainerDesktop: {
    display: {
      xs: 'none',
      md: 'flex',
    },
  },
  input: {
    minHeight: 100,
    width: {
      md: 700,
    },
  },
}));
